import React, { useState,useEffect } from "react";
import * as Yup from "yup";
import { storeAuthToken, storeOrganizerId, storeUserDetails } from '../../utils/auth'
import { useFormik } from "formik";
import PinInput from "react-pin-input";
import { requestOTP,verifyOTP ,registerOrganizer, getAllCountries } from "../../api"
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Lock, Mail, ShieldCheck, User } from 'lucide-react';
import { images } from '../../assets/images';
import Background from '../Login/Background';
import '../Login/LoginPage.css';

type Props = {}

const RegisterPage = (props: Props) => {
  const [otpSent, setOtpSent] = useState(false);
  const [OTP, setOTP] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [registrationId, setRegistrationId] = useState<string | null>(null);
	const [userEmail,setEmail] = useState<string | null>(null);
  const [otpVerified, setOtpVerified] = useState(false);
  const [userDetails, setUserDetails] = useState({ name: "" });
  // Country on Complete Profile (same getAllCountries pattern as venue).
  const [countries, setCountries] = useState<any[]>([]);
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [countriesLoading, setCountriesLoading] = useState(false);
  // Password on Complete Profile (same rules as venue signup).
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [showTermsModal, setShowTermsModal] = useState(false);
  const navigate = useNavigate();
  const [timer, setTimer] = useState(30);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (otpSent && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpSent, timer]);

  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyOverflow;
    };
  }, []);

  // Load countries once the profile step is shown (needed for organizer countryId).
  useEffect(() => {
    if (!otpVerified) return;
    let cancelled = false;
    (async () => {
      try {
        setCountriesLoading(true);
        const res = await getAllCountries();
        if (!cancelled) setCountries(res || []);
      } catch (err) {
        console.error("Failed to load countries:", err);
        if (!cancelled) setError("Failed to load countries. Please refresh and try again.");
      } finally {
        if (!cancelled) setCountriesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [otpVerified]);

  const validation = useFormik({
    initialValues: {
      email: "",
	  acceptTerms:false,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email")
        .required("Email is required"),
      acceptTerms: Yup.boolean()
        .oneOf([true], "Accept the Terms and Conditions to continue")
        .required("Accept the Terms and Conditions to continue"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError("");
        const response = await requestOTP({ email: values.email });
        console.log("OTP request response:", response);
        if (response.registrationId) {
          setRegistrationId(response.registrationId);
        }
		if(response.email){
          setEmail(response.email);
        }
        setOtpSent(true);
      } catch (err:any) {
        console.error("Failed to request OTP:", err);
        setError(err.response?.data?.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
  });
  const handleResendOtp = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await requestOTP({ email: validation.values.email });
      console.log("Resend OTP response:", response);
      if (response.registrationId) {
        setRegistrationId(response.registrationId);
      }
      if (response.email) {
        setEmail(response.email);
      }
      setTimer(60);
    } catch (err: any) {
      console.error("Failed to resend OTP:", err);
      setError(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e: any) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError("");

      const res = await verifyOTP({
        email: validation.values.email,
        otp: OTP,
        registrationId,
      });
      console.log("OTP verification response:", res);
      if (res.verified === true) {
        setOtpVerified(true);
      } else {
        setError("Invalid OTP. Please try again.");
      }
    } catch (err: any) {
      console.error("OTP verification failed:", err);
      setError(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };
  const handleDetailsSubmit = async (e: any) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError("");
      setPasswordError("");
      setConfirmPasswordError("");

      const name = (userDetails.name || "").trim();
      if (!name) {
        setError("Please enter your name");
        return;
      }
      if (!selectedCountryId) {
        setError("Please select a country");
        return;
      }
      if (!password || password.length < 8) {
        setPasswordError("Password must be at least 8 characters");
        return;
      }
      if (password !== confirmPassword) {
        setConfirmPasswordError("Passwords must match");
        return;
      }

      const res = await registerOrganizer({
        registrationId,
        name,
        password,
        countryId: Number(selectedCountryId),
      });
      if (res) {
        storeAuthToken(res.token);
        storeUserDetails(res.user);
        storeOrganizerId(Number(res?.user?.userId));
      }
      console.log("Registration complete:", res);
      localStorage.setItem(
        "userData",
        JSON.stringify({
          email: validation.values.email,
          name,
        })
      );
      navigate("/home");
    } catch (err: any) {
      console.error("Registration failed:", err);
      setError(err.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="login-screen-wrapper">
      <Background />

      <div className="login-card">
        {/* Logo */}
        <div className="logo-container">
          <a href="index.html">
            <img src={images.eventox_dark_logo} alt="Eventox Logo" className="logo-image" />
          </a>
        </div>

        <p className="login-subtitle">
          Sign up to Eventox Organizer Workspace
        </p>

        {/* Secure indicator */}
        {/* <div className="secure-badge">
          <ShieldCheck className="w-5 h-5 flex-shrink-0" style={{ color: '#18C37E' }} />
          <span>Authorized Registration Authority Node active. Connection TLS secured.</span>
        </div> */}

        {/* Main Content Area */}
        {!otpSent ? (
          <form onSubmit={validation.handleSubmit} className="login-form">
            <div className="input-group-custom">
              <label className="label-custom">Your Email*</label>
              <div className="input-wrapper">
                <span className="input-icon-left">
                  <Mail size={16} />
                </span>
                <input
                  className="input-custom"
                  type="email"
                  name="email"
                  placeholder="Enter email address"
                  value={validation.values.email}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                />
              </div>
              {validation.touched.email && validation.errors.email && (
                <p className="error-msg-custom">{validation.errors.email}</p>
              )}
            </div>

            <div className="input-group-custom" style={{ marginTop: '0.25rem' }}>
              <div className="form-check d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                <input
                  style={{
                    width: "16px",
                    height: "16px",
                    accentColor: "#EE3F7C",
                    cursor: "pointer",
                  }}
                  type="checkbox"
                  name="acceptTerms"
                  id="acceptTerms"
                  checked={validation.values.acceptTerms}
                  onChange={(e) =>
                    validation.setFieldValue("acceptTerms", e.target.checked)
                  }
                  onBlur={validation.handleBlur}
                />
                <label className="form-check-label" htmlFor="acceptTerms" style={{ fontSize: '12px', color: 'var(--login-text-secondary)', cursor: 'pointer' }}>
                  I Accept the{" "}
                  <span
                    style={{ cursor: "pointer", textDecoration: "underline", color: "#EE3F7C", fontWeight: '600' }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowTermsModal(true);
                    }}
                  >
                    Terms and Conditions
                  </span>
                </label>
              </div>
              {validation.touched.acceptTerms && validation.errors.acceptTerms && (
                <p className="error-msg-custom" style={{ marginTop: '0.25rem' }}>{validation.errors.acceptTerms}</p>
              )}
            </div>

            <button
              className="btn-submit"
              type="submit"
              disabled={loading || !validation.values.acceptTerms}
              style={{
                opacity: loading || !validation.values.acceptTerms ? 0.6 : 1,
              }}
            >
              {loading ? "Sending OTP..." : "Send OTP"}
            </button>
            {error && <p className="error-msg-custom">{error}</p>}
          </form>
        ) : !otpVerified ? (
          <form onSubmit={handleOtpSubmit} className="login-form">
            <h2 className="registration-title text-center" style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', marginBottom: '0.25rem' }}>Verify your Email</h2>
            <div className="input-group-custom">
              <label className="label-custom" style={{ textAlign: 'center', width: '100%' }}>Enter OTP*</label>
              <div className="otp-input-container">
                <PinInput
                  length={6}
                  secret={false}
                  type="numeric"
                  inputStyle={{
                    width: "40px",
                    height: "48px",
                    margin: "0 6px",
                    borderRadius: "10px",
                    border: "1px solid var(--login-input-border)",
                    background: "var(--login-input-bg)",
                    color: "var(--login-text-primary)",
                    fontSize: "20px",
                    fontWeight: "600",
                    textAlign: "center",
                  }}
                  onChange={(value: any) => setOTP(value)}
                  onComplete={(value: any) => setOTP(value)}
                />
              </div>
            </div>

            <div className="text-center" style={{ fontSize: '13px', marginTop: '0.5rem' }}>
              {timer > 0 ? (
                <p style={{ color: 'var(--login-text-muted)', margin: 0 }}>
                  Re-send available in {timer}s
                </p>
              ) : (
                <p style={{ color: 'var(--login-text-secondary)', margin: 0 }}>
                  Didn’t receive code?{" "}
                  <span
                    style={{ cursor: "pointer", fontWeight: "700", color: "#EE3F7C" }}
                    onClick={handleResendOtp}
                  >
                    Resend
                  </span>
                </p>
              )}
            </div>

            <button className="btn-submit" type="submit" disabled={loading}>
              {loading ? "Verifying OTP..." : "Verify OTP"}
            </button>
            {error && <p className="error-msg-custom">{error}</p>}
          </form>
        ) : (
          <form onSubmit={handleDetailsSubmit} className="login-form">
            <h2 className="registration-title text-center" style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', marginBottom: '0.25rem' }}>Complete Your Profile</h2>
            <div className="input-group-custom">
              <label className="label-custom">Full Name*</label>
              <div className="input-wrapper">
                <span className="input-icon-left">
                  <User size={16} />
                </span>
                <input
                  className="input-custom"
                  type="text"
                  name="name"
                  placeholder="Enter your full name"
                  value={userDetails.name}
                  onChange={(e) => setUserDetails({ ...userDetails, name: e.target.value })}
                />
              </div>
            </div>

            <div className="input-group-custom">
              <label className="label-custom">Country*</label>
              <div className="input-wrapper">
                <select
                  className="input-custom"
                  name="country"
                  value={selectedCountryId}
                  onChange={(e) => {
                    setSelectedCountryId(e.target.value);
                    setError("");
                  }}
                  disabled={countriesLoading}
                  style={{ colorScheme: "dark", paddingLeft: "1rem" }}
                >
                  <option value="" style={{ backgroundColor: "#1a1a1f", color: "#fff" }}>
                    {countriesLoading ? "Loading countries..." : "Select Country"}
                  </option>
                  {countries.map((c: any) => (
                    <option
                      key={c.countryId}
                      value={c.countryId}
                      style={{ backgroundColor: "#1a1a1f", color: "#fff" }}
                    >
                      {c.countryName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="input-group-custom">
              <label className="label-custom">Password*</label>
              <div className="input-wrapper">
                <span className="input-icon-left">
                  <Lock size={16} />
                </span>
                <input
                  className="input-custom"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setPasswordError("");
                  }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="eye-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {passwordError && <p className="error-msg-custom">{passwordError}</p>}
            </div>

            <div className="input-group-custom">
              <label className="label-custom">Confirm Password*</label>
              <div className="input-wrapper">
                <span className="input-icon-left">
                  <Lock size={16} />
                </span>
                <input
                  className="input-custom"
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setConfirmPasswordError("");
                  }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="eye-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {confirmPasswordError && (
                <p className="error-msg-custom">{confirmPasswordError}</p>
              )}
            </div>

            <button className="btn-submit" type="submit" disabled={loading || countriesLoading}>
              {loading ? "Submitting..." : "Finish Signup"}
            </button>
            {error && <p className="error-msg-custom">{error}</p>}
          </form>
        )}

        <div className="register-link-custom" style={{ fontSize: '12px', marginTop: '0.5rem', color: 'var(--login-text-secondary)', lineHeight: '1.4' }}>
          By clicking "Sign up", you agree to EventoX{" "}
          <a href="https://eventox.co/about-us/" target="_blank" rel="noopener noreferrer">Terms & Conditions</a> and have read the{" "}
          <a href="https://eventox.co/about-us/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </div>

        <div className="register-link-custom">
          Already have an account? <a href="/login">Sign In</a>
        </div>

        <div className="text-center" style={{ fontSize: '11px', color: 'var(--login-text-muted)', marginTop: '0.5rem' }}>
          © {new Date().getFullYear()}, Eventox. All rights reserved.
        </div>
      </div>

      {showTermsModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 10000 }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable" style={{ margin: '2rem auto', maxWidth: '800px' }}>
            <div className="modal-content" style={{ background: 'rgba(20, 20, 25, 0.95)', border: '1px solid rgba(255, 255, 255, 0.1)', color: 'white', backdropFilter: 'blur(20px)', borderRadius: '16px' }}>
              <div className="modal-header" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)', padding: '1rem 1.5rem' }}>
                <h5 className="modal-title" style={{ fontWeight: 'bold' }}>EVENTOX ORGANIZER AGREEMENT</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowTermsModal(false)}
                  style={{ filter: 'invert(1)', background: 'none', border: 'none', fontSize: '20px', color: 'white', cursor: 'pointer' }}
                >
                  ✕
                </button>
              </div>

              <div className="modal-body" style={{ whiteSpace: "pre-line", maxHeight: '60vh', overflowY: 'auto', fontSize: '13px', color: 'rgba(255,255,255,0.8)', padding: '1.5rem' }}>
                {`EVENTOX – GLOBAL TERMS & CONDITIONS
(Applicable Worldwide to All Platform Users)

These Terms & Conditions (“Terms”) govern access to and use of the Eventox platform (“Eventox”, “we”, “us”, “our”), operated by Vibra Verse Ltd.

By signing up, accessing, or using Eventox in any capacity, you agree to these Terms.

⸻

1. WHO THESE TERMS APPLY TO

These Terms apply to all individuals and entities using Eventox, including but not limited to:
  •	Event attendees / ticket buyers (“Users”)
  •	Event organizers (“Organizers”)
  •	Venue owners or venue managers (“Venues”)
  •	Partners, sponsors, collaborators
  •	Media, creators, contributors, and photographers

Each role carries specific responsibilities outlined below.

⸻

2. PLATFORM ROLE & LIMITATION

Eventox is a technology platform providing tools for:
  •	Event discovery and listings
  •	Ticketing and registration
  •	Promotion and marketing
  •	Event management and analytics
  •	Digital content display

Eventox:
  •	Is not an event host, promoter, venue, or organizer
  •	Does not control event execution
  •	Is not responsible for the quality, safety, legality, or outcome of any event

⸻

3. ACCOUNT REGISTRATION & ELIGIBILITY

By creating an account, you confirm that:
  •	You are legally permitted to use the platform in your jurisdiction
  •	All information provided is accurate and up to date
  •	You will keep your account credentials secure

Eventox may suspend or terminate accounts providing false, misleading, or unlawful information.

⸻

4. ROLE-SPECIFIC RESPONSIBILITIES

4.1 Users / Attendees
  •	Tickets are subject to the Organizer’s event rules and refund policies
  •	Eventox is not responsible for:
  •	Event cancellations or postponements
  •	Artist no-shows
  •	Venue changes or conditions
  •	Disputes should be raised directly with the Organizer

⸻

4.2 Organizers

Organizers are solely responsible for:
  •	Event legality and compliance with local laws
  •	Licenses, permits, and approvals
  •	Event execution, safety, staffing, and security
  •	Refunds, disputes, and chargebacks

Organizers must not:
  •	Transfer events to other platforms without valid reason
  •	Misuse Eventox systems or data
  •	Share attendee or lead data without proper consent

⸻

4.3 Venue Owners / Managers

Venues confirm that:
  •	Venue details and availability are accurate
  •	They have authority to list the venue
  •	Agreements with Organizers are handled independently

Eventox is not liable for venue-related disputes, damages, or losses.

⸻

4.4 Partners, Sponsors & Media

Partners and media contributors:
  •	Are responsible for their own content and representations
  •	Must obtain consent before collecting or using any user data
  •	Must not misuse Eventox branding or platform data

Eventox is not responsible for partner conduct or claims.

⸻

5. PAYMENTS, FEES & PAYOUTS
  •	Platform and service fees apply where relevant
  •	Organizers receive payouts after submitting a payout request
  •	All payouts are processed within 5 working days, subject to:
  •	Dispute resolution
  •	Chargebacks
  •	Fraud or compliance reviews

Eventox may delay payouts where necessary for security or legal reasons.

⸻

6. CUSTOM SERVICES & ADDITIONAL WORK

Any custom services (including design changes, integrations, manual work, promotions):
  •	Must be agreed in advance
  •	Will be charged as discussed
  •	Are payable after the event unless agreed otherwise in writing

⸻

7. DATA, PRIVACY & CONFIDENTIALITY

All users agree to:
  •	Comply with applicable data protection and privacy laws in their jurisdiction
  •	Keep confidential any non-public platform information
  •	Not sell, misuse, or share data without proper consent

Sharing attendee or lead data with sponsors or third parties requires explicit consent from the data subject.

⸻

8. MARKETING & CONTENT RIGHTS

By using Eventox, you grant Eventox a non-exclusive, royalty-free right to:
  •	Display your event, venue, or content
  •	Use names, logos, and images for platform promotion

Content ownership remains with the original owner.

⸻

9. SECURITY, HACKING & FORCE MAJEURE

Eventox applies reasonable, industry-standard security practices. However, no platform can guarantee uninterrupted or error-free service.

Eventox is not liable for losses caused by:
  •	Cyberattacks or hacking
  •	Malware or system failures
  •	Third-party service outages
  •	Internet or infrastructure disruptions
  •	Events beyond reasonable control

Eventox will take appropriate corrective action where legally required.

⸻

10. PLATFORM MISUSE & ENFORCEMENT

Eventox may suspend or terminate accounts involved in:
  •	Fraud or misrepresentation
  •	Data misuse
  •	Abuse, harassment, or illegal activity
  •	Violations of these Terms

Outstanding obligations remain enforceable after termination.

⸻

11. LIMITATION OF LIABILITY

To the maximum extent permitted by applicable law:
  •	Eventox is not liable for indirect or consequential losses
  •	Liability is limited to platform fees paid (if any)
  •	Eventox does not guarantee event success or user satisfaction

⸻

12. INDEMNIFICATION

You agree to indemnify and hold Eventox harmless from claims, losses, or damages arising from:
  •	Your actions or omissions
  •	Your events, content, or services
  •	Breach of these Terms or applicable laws

⸻

13. GOVERNING LAW & JURISDICTION (GLOBAL)

These Terms shall be governed by and interpreted in accordance with the laws applicable in the jurisdiction where Eventox is registered, unless otherwise required by mandatory local consumer laws.

⸻

14. UPDATES TO TERMS

Eventox may update these Terms from time to time.
Continued use of the platform indicates acceptance of the updated Terms.

⸻

15. ACCEPTANCE

By ticking the checkbox at signup, you confirm that:
  •	You have read and understood these Terms
  •	You agree to comply with them
  •	You accept responsibility for your role on the platform`}
              </div>

              <div className="modal-footer" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', padding: '1rem 1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowTermsModal(false)}
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Close
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    validation.setFieldValue("acceptTerms", true);
                    setShowTermsModal(false);
                  }}
                  style={{ background: 'linear-gradient(135deg, #EE3F7C 0%, #F79B59 100%)', border: 'none', color: 'white', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  I Agree
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
